import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DistributionSlice } from '../../core/models/dashboard.model';
import { formatarNumero } from '../../core/services/dashboard-metrics.util';

export type DistributionBarMode = 'stacked' | 'rows';

/** Item já pronto para o template: largura resolvida, cor e texto de exibição. */
interface DistributionBarItem {
  slice: DistributionSlice;
  /** 0–100. No modo `stacked` já passou pelo mínimo garantido + reescala. */
  widthPercent: number;
  color: string;
  displayValue: string;
  clickable: boolean;
  accessibleLabel: string;
}

/**
 * Paleta padrão usando tokens semânticos do tema (não cores fixas), para que
 * a barra funcione nos dois temas sem configuração extra. Cinco tons: acima
 * disso, o índice recicla — a separação entre segmentos vizinhos (ver SCSS)
 * garante que dois segmentos adjacentes com a mesma cor da paleta continuem
 * distinguíveis, então o reciclo não quebra a leitura.
 */
const DEFAULT_PALETTE = [
  'var(--state-info)',
  'var(--state-success)',
  'var(--state-warning)',
  'var(--state-danger)',
  'var(--v-border-strong)',
];

/**
 * Abaixo disso um segmento `stacked` vira uma tira de poucos pixels — alvo de
 * clique ruim demais para caber num toque de dedo ou até num clique preciso
 * de mouse. Todo segmento com contagem > 0 recebe pelo menos essa largura.
 */
const MIN_STACKED_PERCENT = 4;

/**
 * Mostra como um total se reparte entre categorias, cada fatia sendo um alvo
 * de clique de verdade — por isso barra em vez de gráfico de rosca (fatia
 * fina de rosca é alvo de clique ruim). Não conhece chamado, título a
 * receber ou pedido: reutilizável por qualquer dashboard derivado de
 * listagem (chamados, geral, financeiro, vendas).
 *
 * Dois modos:
 * - `stacked`: uma única barra horizontal, cada fatia ocupa sua `percent` da
 *   largura. Para distribuição por status.
 * - `rows`: uma linha por fatia, rótulo à esquerda, barra proporcional e o
 *   número à direita. Para carga por pessoa e faixas de envelhecimento.
 *
 * Clicável de verdade somente quando a fatia tem `target`; nesse caso
 * renderiza um `<button>` real (foco e ativação por teclado nativos, sem
 * `role`/`tabindex` manuais). Sem destino, renderiza um elemento inerte.
 */
@Component({
  selector: 'app-distribution-bar',
  standalone: true,
  templateUrl: './distribution-bar.component.html',
  styleUrls: ['./distribution-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DistributionBarComponent {
  readonly slices = input.required<DistributionSlice[]>();
  readonly mode = input<DistributionBarMode>('stacked');
  readonly palette = input<string[]>(DEFAULT_PALETTE);

  readonly sliceClick = output<DistributionSlice>();

  private readonly resolvedPalette = computed(() =>
    this.palette().length ? this.palette() : DEFAULT_PALETTE
  );

  /**
   * Larguras para o modo `stacked`: cada fatia com contagem > 0 recebe pelo
   * menos `MIN_STACKED_PERCENT`. Se a soma dos mínimos estourar 100% (ex.:
   * dez fatias de 1% cada viram dez de 4% = 40%, mas some com o resto e pode
   * passar de 100), tudo é reescalado proporcionalmente para caber de volta
   * em 100% — a fatia continua clicável, só perde um pouco de precisão
   * visual na proporção exata, o que é aceitável porque o número exato já
   * está no texto (legenda / aria-label), não só na largura do pedaço.
   */
  readonly stackedItems = computed<DistributionBarItem[]>(() => {
    const slices = this.slices();
    if (!slices.length) {
      return [];
    }
    const palette = this.resolvedPalette();
    const rawWidths = slices.map(slice => this.clampPercent(slice.percent));
    const minWidths = rawWidths.map(width =>
      width > 0 ? Math.max(width, MIN_STACKED_PERCENT) : 0
    );
    const total = minWidths.reduce((sum, width) => sum + width, 0);
    const scale = total > 100 ? 100 / total : 1;
    return slices.map((slice, index) =>
      this.toItem(slice, minWidths[index] * scale, palette, index)
    );
  });

  /**
   * Larguras para o modo `rows`: proporcional ao `percent` da própria fatia,
   * sem mínimo forçado — o alvo de clique é a linha inteira (rótulo + barra
   * + número), não a barrinha, então uma barra fininha não é um problema de
   * acessibilidade aqui como é no `stacked`.
   */
  readonly rowItems = computed<DistributionBarItem[]>(() => {
    const slices = this.slices();
    const palette = this.resolvedPalette();
    return slices.map((slice, index) =>
      this.toItem(slice, this.clampPercent(slice.percent), palette, index)
    );
  });

  onActivate(item: DistributionBarItem): void {
    if (item.clickable) {
      this.sliceClick.emit(item.slice);
    }
  }

  private toItem(
    slice: DistributionSlice,
    widthPercent: number,
    palette: string[],
    index: number
  ): DistributionBarItem {
    // `formatarNumero`, não `String()`: sem isso uma fatia sem
    // `formattedValue` exibia "1390" em vez de "1.390" — e um peso decimal
    // (soma em dinheiro sem formatador) sairia com ponto americano.
    const displayValue = slice.formattedValue ?? formatarNumero(slice.count);
    return {
      slice,
      widthPercent,
      color: palette[index % palette.length],
      displayValue,
      clickable: !!slice.target,
      accessibleLabel: `${slice.label}: ${displayValue}`,
    };
  }

  private clampPercent(percent: number): number {
    return Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
  }
}
